import type { RuleModule } from '../engine/registry';
import type { GameEventDraft, GameRuntime, GameState } from '../engine/state';
import type { AdvanceBag } from './advanceCards';
import type { SeatIdx } from '../types';

// The two transferable award tiles (outline §11, §14.13/14). Longest Route is the
// longest simple TRAIL over a seat's roads (each road once; loops count; forks take one
// arm; own buildings never sever; any OTHER owner's building blocks pass-through but a
// trail may end there). Claims at ≥routeMin; ties keep the holder; strictly-longer
// transfers; on a break: holder keeps if still longest-or-tied at ≥min, else a unique
// ≥min leader takes it, else the tile LEAVES PLAY (holder null) and re-awards to the
// next unique ≥min leader. Muster counts FACE-UP militia only. Award math is purely
// objective over the board — resigned seats' networks still hold and deny (D5: pieces
// stay). Recomputed after every action (afterAction), so transfers land mid-anyone-
// action, before the victory module's check in module order (outline §11).

export interface AwardsBag {
  longestRoute: { holder: SeatIdx | null };
  muster: { holder: SeatIdx | null };
}

/**
 * Longest simple trail over `seat`'s roads (outline §14.13 semantics).
 *
 * Takes the BOARD rather than the whole state (D192): it only ever read `board.edges` and
 * `board.vertices`, and a `GameView` carries exactly that shape. The client shows the viewer
 * their own route length on the shelf, and it must be the same number the award is decided
 * by — so it calls this, rather than reimplementing a graph walk that could disagree with
 * the engine about what counts as a trail.
 */
export function routeLength(board: GameState['board'], env: GameRuntime, seat: SeatIdx): number {
  // H20-S2: every EDGE piece the def declares — roads, and ships where a sea def composes them.
  // A trail may change piece kind only at a corner holding the seat's OWN building (a road and a
  // ship meeting at an empty corner are two networks, not one); with only roads on the board
  // that rule can never fire, which T-SEA-013 proves differentially over the whole golden.
  const edgePieces = new Set(env.def.pieces.filter((p) => p.domain === 'edge').map((p) => p.id));
  const segs: Array<[string, string, string]> = [];
  for (const [eid, p] of Object.entries(board.edges)) {
    if (p.owner !== seat || !edgePieces.has(p.piece)) continue;
    const e = env.board.edges.get(eid);
    if (e) segs.push([e.vertices[0], e.vertices[1], p.piece]);
  }
  if (segs.length === 0) return 0;
  const adj = new Map<string, number[]>();
  segs.forEach(([a, b], i) => {
    if (!adj.has(a)) adj.set(a, []);
    if (!adj.has(b)) adj.set(b, []);
    adj.get(a)!.push(i);
    adj.get(b)!.push(i);
  });
  // a vertex severs pass-through iff some OTHER owner (opponent or neutral) built there
  const severed = (v: string): boolean => {
    const p = board.vertices[v];
    return p !== undefined && p.owner !== seat;
  };
  const ownBuilding = (v: string): boolean => board.vertices[v]?.owner === seat;
  let best = 0;
  const used: boolean[] = segs.map(() => false);
  const dfs = (v: string, len: number, kind: string | null): void => {
    if (len > best) best = len;
    if (len > 0 && severed(v)) return; // may END here, never continue through
    for (const i of adj.get(v) ?? []) {
      if (used[i]) continue;
      const [a, b, piece] = segs[i]!;
      if (kind !== null && piece !== kind && !ownBuilding(v)) continue; // road↔ship only via your own building
      used[i] = true;
      dfs(a === v ? b : a, len + 1, piece);
      used[i] = false;
    }
  };
  for (const start of adj.keys()) dfs(start, 0, null);
  return best;
}

/** Re-derive both award holders from the board; emits AWARD_MOVED on changes. */
export function recomputeAwards(state: GameState, env: GameRuntime, emit: (e: GameEventDraft) => void): void {
  const bag = state.bags['awards'] as AwardsBag;
  const seats = state.seats.map((_, i) => i);

  // --- Longest Route (outline §11 + the three break outcomes §14.13) ---
  const lens = seats.map((s) => routeLength(state.board, env, s));
  const min = env.cfg.engine.routeMin;
  const best = Math.max(...lens);
  const leaders = seats.filter((s) => lens[s] === best);
  const h = bag.longestRoute.holder;
  let next: SeatIdx | null;
  if (h !== null && lens[h]! >= min && lens[h]! >= best) {
    next = h; // still longest or tied ⇒ keeps
  } else if (best >= min && leaders.length === 1) {
    next = leaders[0]!; // fresh claim or unique transfer (§14.13 case 2)
  } else {
    next = null; // unclaimed, nobody ≥min, or non-holders tied for best (§14.13 case 3)
  }
  if (next !== h) {
    bag.longestRoute.holder = next;
    emit({ kind: 'AWARD_MOVED', vis: 'public', award: 'longest-route', from: h, to: next });
  }

  // --- Muster (outline §10/§14.14): face-up militia only; strictly-more transfers ---
  const adv = state.bags['advance-cards'] as AdvanceBag | undefined;
  if (adv !== undefined) {
    const counts = seats.map((s) => adv.perSeat[s]!.faceUpMilitia);
    const mh = bag.muster.holder;
    const mBest = Math.max(...counts);
    const mLeaders = seats.filter((s) => counts[s] === mBest);
    let mNext = mh;
    if (mh === null) {
      if (mBest >= env.cfg.engine.musterMin && mLeaders.length === 1) mNext = mLeaders[0]!;
    } else if (mBest > counts[mh]! && mLeaders.length === 1) {
      mNext = mLeaders[0]!; // strictly more than the holder; the tile never leaves play
    }
    if (mNext !== mh) {
      bag.muster.holder = mNext;
      emit({ kind: 'AWARD_MOVED', vis: 'public', award: 'muster', from: mh, to: mNext });
    }
  }
}

export const awardsModule: RuleModule = {
  id: 'awards',
  register(ctx) {
    ctx.bag({
      init() {
        return { longestRoute: { holder: null }, muster: { holder: null } } satisfies AwardsBag;
      },
      clone(bag) {
        const b = bag as AwardsBag;
        return {
          longestRoute: { holder: b.longestRoute.holder },
          muster: { holder: b.muster.holder },
        } satisfies AwardsBag;
      },
    });
    ctx.afterAction((state, env, emit) => recomputeAwards(state, env, emit));
    // 'awards' VP source (outline §12): each held tile scores vp.award
    ctx.vpSource('awards', (state, env, seat) => {
      const bag = state.bags['awards'] as AwardsBag;
      const held = (bag.longestRoute.holder === seat ? 1 : 0) + (bag.muster.holder === seat ? 1 : 0);
      return held * env.cfg.engine.vp.award;
    });
  },
};
